import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend, PieChart, Pie, ComposedChart, Line,
} from "recharts";
import { formatMoney, formatNumber, ABCD_COLORS } from "../../lib/api";

const GRUPO_COLOR = { alta: "#059669", baja: "#f59e0b", parado: "#dc2626" };
export const pctES = (v) => (v == null ? "—" : `${String(v).replace(".", ",")} %`);

export function Card({ title, sub, children, testid, right }) {
  return (
    <div className="card p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display text-lg font-bold">{title}</h3>
          {sub && <p className="text-xs text-slate-500">{sub}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function AbcdChart({ rows }) {
  return (
    <Card title="ABCD del laboratorio" sub="% del stock frente al % de las ventas en cada clase." testid="lab-abcd-chart">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={rows} barGap={3}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="code" tick={{ fontSize: 12, fontWeight: 600 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} %`} />
          <Tooltip formatter={(v, n, it) => [`${pctES(v)} · ${formatMoney(n === "% stock" ? it.payload.stock : it.payload.ventas)}`, n]} />
          <Legend wrapperStyle={{ fontSize: 11 }} payload={[{ value: "% stock", type: "square", color: "#94a3b8" }, { value: "% ventas (color de cada clase)", type: "square", color: "#10b981" }]} />
          <Bar dataKey="pct_stock" name="% stock" fill="#94a3b8" radius={[4, 4, 0, 0]} />
          <Bar dataKey="pct_ventas" name="% ventas" radius={[4, 4, 0, 0]}>
            {rows.map((r) => <Cell key={r.code} fill={ABCD_COLORS[r.code]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

function Donut({ rows, k, title }) {
  return (
    <div className="flex-1 min-w-[180px]">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold text-center">{title}</div>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie data={rows} dataKey={k} nameKey="label" innerRadius={45} outerRadius={70} paddingAngle={2}>
            {rows.map((g) => <Cell key={g.key} fill={GRUPO_COLOR[g.key]} />)}
          </Pie>
          <Tooltip formatter={(v) => formatMoney(v)} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TipoStock({ grupos }) {
  return (
    <Card title="Tipo de stock y ventas de cada tipo" sub="Alta rotación (AA–B), baja rotación (CC–C) y stock parado (D)." testid="lab-tipo-stock">
      <div className="flex flex-wrap">
        <Donut rows={grupos} k="stock" title="Stock" />
        <Donut rows={grupos} k="ventas" title="Ventas" />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2">
        {grupos.map((g) => (
          <div key={g.key} className="rounded-lg border-l-4 bg-slate-50 px-3 py-2" style={{ borderColor: GRUPO_COLOR[g.key] }} data-testid={`lab-grupo-${g.key}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: GRUPO_COLOR[g.key] }}>{g.label}</div>
            <div className="text-xs mt-1">Stock <b>{formatMoney(g.stock)}</b> · {pctES(g.pct_stock)}</div>
            <div className="text-xs">Ventas <b>{formatMoney(g.ventas)}</b> · {pctES(g.pct_ventas)}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function Mensual({ rows }) {
  return (
    <Card title="Ventas mes a mes" sub="Facturación de los productos de este laboratorio en cada mes subido." testid="lab-mensual">
      {rows.length === 0 ? <p className="text-sm text-slate-500">Aún no hay meses de ventas subidos con estos productos.</p> : (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="l" tick={{ fontSize: 11 }} tickFormatter={(v) => formatMoney(v)} width={80} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, n) => (n === "Unidades" ? formatNumber(v) : formatMoney(v))} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="l" dataKey="ventas" name="Ventas" fill="#0f172a" radius={[4, 4, 0, 0]} />
            <Line yAxisId="r" dataKey="uds" name="Unidades" stroke="#10b981" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}
